import { Box, TextField, Typography } from "@mui/material"
import { useEffect, useRef, useState } from "react"
import { IMaskInput } from "react-imask"
import { formatTime } from "../../../../../helpers/timeline.helpers"
import { useCurrentTime } from "../../../../../hooks/useCurrentTime"

interface Props {
    isValid: boolean
    frame: number
    formattedDuration: string
    pause: (time?: number) => void
    handleTimeChange: (value: string) => void
}

/**
 * The "current time / duration" readout, split out of `TimelineControlTools`.
 *
 * This is the only part of the control bar that depends on the playhead, so it is the only
 * one that subscribes to `useCurrentTime`. Keeping it here means the surrounding toolbar —
 * with its menus, tooltips and zoom slider — is not re-rendered ~50x per second.
 *
 * The edited string is local state: it only exists while the field is open, and playback is
 * paused on entering edit mode, so it never has to be reconciled with a moving playhead.
 */
const TimelineTimeField = ({
    isValid,
    frame,
    formattedDuration,
    pause,
    handleTimeChange,
}: Props) => {
    const time = useCurrentTime()
    const [isEditing, setIsEditing] = useState(false)
    const [inputTime, setInputTime] = useState<string>("0:00:00:00")
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (isEditing && inputRef.current) inputRef.current.focus()
    }, [isEditing])

    const handleOnKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault()
            if (isValid) {
                handleTimeChange(inputTime)
            }
            setIsEditing(false)
        } else if (e.key === "Escape") {
            e.preventDefault()
            setIsEditing(false)
        }
    }

    return (
        <Box display="flex" alignItems="center" gap={0.5}>
            {isEditing ? (
                <TextField
                    ref={inputRef}
                    type="text"
                    value={inputTime}
                    autoFocus
                    onChange={(e) => {
                        setInputTime(e.target.value)
                        handleTimeChange(e.target.value)
                    }}
                    onBlur={() => {
                        setIsEditing(false)
                        if (isValid) {
                            handleTimeChange(inputTime)
                        }
                    }}
                    onKeyDown={handleOnKey}
                    InputProps={{
                        inputComponent: IMaskInput,
                        inputProps: {
                            mask: "0:00:00:00",
                            definitions: { "#": /[0-9]/ },
                            overwrite: true,
                        },
                    }}
                    sx={{ width: 72 }}
                    size="small"
                    variant="standard"
                    error={!isValid}
                />
            ) : (
                <Typography
                    onClick={() => {
                        pause()
                        setIsEditing(true)
                        setInputTime(formatTime(time, frame))
                    }}
                    sx={{
                        cursor: "pointer",
                        fontVariantNumeric: "tabular-nums",
                    }}
                >
                    {formatTime(time, frame)}
                </Typography>
            )}
            <Typography sx={{ fontVariantNumeric: "tabular-nums" }}>
                / {formattedDuration}
            </Typography>
        </Box>
    )
}

export default TimelineTimeField
